import { Link } from 'react-router-dom';
import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import DifficultyIcons from '../components/DifficultyIcons';
import Icon from '../components/Icon';
import BookingModal from '../components/BookingModal';
import QuestionModal from '../components/QuestionModal';
import AutoCarousel from '../components/AutoCarousel';
import FaqPreview from '../components/FaqPreview';
import PriceBanner from '../components/PricePopup';
import { injectIymcsMark } from '../utils/richText';
import { useSeo } from '../hooks/useSeo';
import '../styles/races_page.css';

const safe = (html) => ({ __html: injectIymcsMark(DOMPurify.sanitize(html || '')) });

const Races = ({ isPreview = false }) => {
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  useSeo({
    title: 'Регаты',
    description: 'Участие в парусных регатах любого уровня — от любительских до профессиональных. Подготовка экипажа и аренда яхт.',
  });

  const source = isPreview ? cards : productionCards;
  const races = (source.races || []).filter(c => c.isPublished === true);
  const cardLink = (card) => isPreview ? `/preview/races/${card.slug || card.id}` : `/races/${card.slug || card.id}`;
  const galleryImages = races.flatMap(c => c.gallery || []).filter(Boolean);
  const hasAnyPrice = races.some(c => c.price);

  return (
    <main className="races-page">
      {hasAnyPrice && <PriceBanner isPreview={isPreview} />}
      <section
        className="races-hero"
        style={content.racesHeroImage ? {
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55),rgba(0,0,0,0.55)), url(${content.racesHeroImage})`
        } : {}}
      >
        <div className="hero-content">
          <h1>Парусные регаты</h1>
          <p className="subtitle">
            Профессиональные соревнования и любительские гонки для всех уровней подготовки
          </p>
        </div>
      </section>
      {!content.racesFeaturesBelow && (content.racesFeaturesTitle || (content.racesFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.racesFeaturesTitle && <h2>{content.racesFeaturesTitle}</h2>}
            {content.racesFeaturesText && <div className={content.racesFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.racesFeaturesText)} />}
            {(content.racesFeatures || []).length > 0 && (
              <div className="features-grid">
                {(content.racesFeatures || []).map(f => (
                  <div key={f.id} className="feature">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <section className="races-list">
        <h2>Ближайшие регаты</h2>
        <div className={`cards-list${races.length === 1 ? ' cards-list--single' : ''}`}>
          {races.map(race => (
            <Link key={race.id} to={cardLink(race)} className="race-card">
              {race.image && (<div className="card-image" style={{ backgroundImage: `url(${race.image})` }}></div>)}
              <div className="card-content">
                <h3>{race.title}</h3>
                <div className="card-meta">
                  {race.date && (<span><Icon name="calendar" size={14}/> {race.date}</span>)}
                  {race.miles && (<span><Icon name="miles" size={14}/> {race.miles} миль</span>)}
                  {race.location && (<span><Icon name="pin" size={14}/> {race.location}</span>)}
                  {race.type && (<span><Icon name="flag" size={14}/> {race.type}</span>)}
                  {race.price && (<span className="card-price-link"><Icon name="money" size={14}/> {race.price}</span>)}
                  {(race.badges || []).map((b, i) => (
                    <span key={`badge-${i}`}><Icon name={b.icon} size={14}/> {b.text}</span>
                  ))}
                  {race.difficulty > 0 && (
                    <DifficultyIcons difficulty={race.difficulty} sectionKey="races" />
                  )}
                </div>
                <div className="card-desc-html" dangerouslySetInnerHTML={safe(race.description)} />
                {race.requirements && (
                  <div className="card-highlights">
                    <h4>Условия участия:</h4>
                    <ul>
                      {race.requirements.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <span className="details-link">Подробнее о регате →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      {content.racesFeaturesBelow && (content.racesFeaturesTitle || (content.racesFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.racesFeaturesTitle && <h2>{content.racesFeaturesTitle}</h2>}
            {content.racesFeaturesText && <div className={content.racesFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.racesFeaturesText)} />}
            {(content.racesFeatures || []).length > 0 && (
              <div className="features-grid">
                {(content.racesFeatures || []).map(f => (
                  <div key={f.id} className="feature">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <FaqPreview faqItems={content.faqItems || []} category="race" faqPath={isPreview ? '/preview/faq' : '/faq'} />
      {galleryImages.length > 0 && (
        <AutoCarousel images={galleryImages} title="Фотогалерея" interval={4500} />
      )}

      <section className="cta-section">
        <h2>{content.racesCtaTitle || 'Остались вопросы?'}</h2>
        {content.racesCtaText && <div className={content.racesCtaTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.racesCtaText)} />}
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>
          Задать вопрос
        </button>
      </section>
      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Заявка на участие в регате"
      />
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Регаты" />
    </main>
  );
};

export default Races;
